import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tag, X, Check, Loader2 } from 'lucide-react';
import { validateDiscountCode, DiscountValidationResult } from '@/services/discountCodeService';

interface PromoCodeInputProps {
  orderAmount: number;
  orderType?: 'bookings' | 'courses';
  onDiscountApplied: (result: DiscountValidationResult | null) => void;
}

export const PromoCodeInput = ({ 
  orderAmount, 
  orderType = 'bookings',
  onDiscountApplied 
}: PromoCodeInputProps) => {
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<DiscountValidationResult | null>(null);

  const handleApplyCode = async () => {
    if (!code.trim()) return;

    setIsValidating(true);
    const result = await validateDiscountCode(code, orderAmount, orderType);
    setValidationResult(result);
    
    if (result.isValid) {
      onDiscountApplied(result);
    } else {
      onDiscountApplied(null);
    }
    
    setIsValidating(false);
  };

  const handleRemoveCode = () => {
    setCode('');
    setValidationResult(null);
    onDiscountApplied(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyCode();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Tag className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Promo Code</span>
        <span className="text-xs text-muted-foreground">(optional)</span>
      </div>
      
      {validationResult?.isValid ? (
        <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/30">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-sm font-medium text-green-500">
              {code.toUpperCase()}
            </span>
            <span className="text-xs text-muted-foreground">
              -{validationResult.discountAmount?.toFixed(2)} EGP
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRemoveCode}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            placeholder="Enter promo code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={handleKeyDown}
            className="uppercase"
            disabled={isValidating}
          />
          <Button
            variant="outline"
            onClick={handleApplyCode}
            disabled={!code.trim() || isValidating}
          >
            {isValidating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Apply'
            )}
          </Button>
        </div>
      )}

      {validationResult && !validationResult.isValid && (
        <p className="text-xs text-destructive">{validationResult.error}</p>
      )}
    </div>
  );
};
